import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { User, Phone, Briefcase, Edit2, MessageCircle } from 'lucide-react';

const fmtINR = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

/**
 * Employee Profile dialog. Shows staff details + monthly attendance counts.
 * Props:
 *   profileStaff: { id, name, phone, role?, monthly_salary?, joining_date? } | null
 *   selectedMonth: 'YYYY-MM'
 *   monthReport: { present, absent, half_day }
 *   onClose: () => void
 *   onEdit: () => void
 *   onWhatsapp: () => void
 */
const EmployeeProfileDialog = ({ profileStaff, selectedMonth, monthReport, onClose, onEdit, onWhatsapp }) => {
  const monthly = parseFloat(profileStaff?.monthly_salary) || 0;
  const monthLabel = selectedMonth
    ? new Date(selectedMonth + '-01').toLocaleDateString('en-IN', { month: 'long', year: 'numeric' })
    : '';

  return (
    <Dialog open={!!profileStaff} onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="profile-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <User className="h-5 w-5 text-blue-600" />Employee Profile
          </DialogTitle>
        </DialogHeader>
        {profileStaff && (
          <div className="space-y-3">
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-4 rounded-lg border border-blue-100">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-blue-600 text-white flex items-center justify-center text-lg font-bold">
                  {profileStaff.name?.charAt(0)?.toUpperCase()}
                </div>
                <div>
                  <div className="text-lg font-semibold text-slate-900">{profileStaff.name}</div>
                  <div className="text-xs text-slate-500 flex items-center gap-2 flex-wrap">
                    <span className="flex items-center gap-1"><Phone className="h-3 w-3" />{profileStaff.phone}</span>
                    {profileStaff.role && (
                      <span className="flex items-center gap-1"><Briefcase className="h-3 w-3" />{profileStaff.role}</span>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="p-3 bg-slate-50 rounded-lg">
                <div className="text-xs text-slate-500">Monthly Salary</div>
                <div className="font-semibold text-slate-900">{fmtINR(monthly)}</div>
              </div>
              <div className="p-3 bg-slate-50 rounded-lg">
                <div className="text-xs text-slate-500">Joining Date</div>
                <div className="font-semibold text-slate-900">{profileStaff.joining_date || '-'}</div>
              </div>
            </div>

            <div className="pt-2">
              <div className="text-xs text-slate-500 mb-2">Attendance — {monthLabel}</div>
              <div className="grid grid-cols-3 gap-2">
                <div className="p-3 bg-green-50 rounded-lg text-center">
                  <div className="text-xs text-green-700">Present</div>
                  <div className="text-xl font-bold text-green-800">{monthReport.present}</div>
                </div>
                <div className="p-3 bg-amber-50 rounded-lg text-center">
                  <div className="text-xs text-amber-700">Half Day</div>
                  <div className="text-xl font-bold text-amber-800">{monthReport.half_day}</div>
                </div>
                <div className="p-3 bg-red-50 rounded-lg text-center">
                  <div className="text-xs text-red-700">Absent</div>
                  <div className="text-xl font-bold text-red-800">{monthReport.absent}</div>
                </div>
              </div>
            </div>

            <div className="flex gap-2 pt-2">
              <Button variant="outline" className="flex-1" onClick={onEdit}>
                <Edit2 className="h-4 w-4 mr-1" />Edit Details
              </Button>
              <Button className="flex-1 bg-green-600 hover:bg-green-700 text-white" onClick={onWhatsapp}>
                <MessageCircle className="h-4 w-4 mr-1" />WhatsApp
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default EmployeeProfileDialog;
